import { showBladeToast } from "@/shared/toast";

type CompareFavoriteWindowFlag = {
    enabled: boolean;
};

declare global {
    interface Window {
        __COMPARE_FAVORITE__?: CompareFavoriteWindowFlag;
    }
}

const BOUND_ATTR = "data-compare-favorite-bound";

function csrfTokenFromMeta(): string {
    return (
        document.querySelector<HTMLMetaElement>(
            'meta[name="csrf-token"]',
        )?.content ?? ""
    );
}

function isCompareFavoriteEnabled(): boolean {
    return window.__COMPARE_FAVORITE__?.enabled === true;
}

function vehicleIdFromButton(btn: HTMLButtonElement): number | null {
    const vehicleId = Number(btn.dataset.vehicleId);
    if (!Number.isFinite(vehicleId) || vehicleId <= 0) {
        return null;
    }

    return vehicleId;
}

/**
 * POST /api/compare-favorite then open the comparateur URL in a new tab.
 */
export async function submitCompareFavorite(vehicleId: number): Promise<void> {
    const response = await fetch("/api/compare-favorite", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-TOKEN": csrfTokenFromMeta(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ vehicle_id: vehicleId }),
    });

    if (!response.ok) {
        throw new Error("compare-favorite failed");
    }

    const data = (await response.json()) as { url?: string };
    if (data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
    }
}

async function onCompareFavoriteClick(
    btn: HTMLButtonElement,
    event: Event,
): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (!isCompareFavoriteEnabled()) {
        return;
    }

    const vehicleId = vehicleIdFromButton(btn);
    if (vehicleId === null) {
        return;
    }

    try {
        await submitCompareFavorite(vehicleId);
        showBladeToast("Véhicule ajouté au comparateur");
    } catch {
        showBladeToast("Erreur, veuillez réessayer", "error");
    }
}

/**
 * Binds click handlers on compare-favorite buttons inside `root`.
 * Safe to call after InstantSearch re-renders (idempotent per button).
 */
export function bindCompareFavoriteButtons(
    root: ParentNode = document,
): void {
    if (!isCompareFavoriteEnabled()) {
        return;
    }

    root.querySelectorAll<HTMLButtonElement>(
        '[data-action="compare-favorite"]',
    ).forEach((btn) => {
        if (btn.hasAttribute(BOUND_ATTR)) {
            return;
        }

        btn.setAttribute(BOUND_ATTR, "true");
        btn.addEventListener("click", (event) => {
            void onCompareFavoriteClick(btn, event);
        });
    });
}

/**
 * Document-level delegate for Blade cards (fallback if a button was not bound).
 */
export function initCompareFavorite(): void {
    document.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement)?.closest<HTMLButtonElement>(
            '[data-action="compare-favorite"]',
        );
        if (!btn || btn.hasAttribute(BOUND_ATTR)) {
            return;
        }

        void onCompareFavoriteClick(btn, e);
    });

    bindCompareFavoriteButtons();
}
